






































































































import {computed, defineComponent, PropType, ref} from "@vue/composition-api";
import ProjectLayout from "@tool/Pages/App/Project/layout/ProjectLayout.vue";
import {ProjectEntityInterface, TemplateEntityInterface} from "@tool/tool.types";
import useInertiaTable from "@tool/Helpers/useInertiaTable";
import {ResourceCollectionInterface, WithProps} from "@base/base.types";
import InertiaDatatable from "@base/Components/InertiaDatatable.vue";
import ThemeBlock from "@base/Components/theme/ThemeBlock.vue";
import DisplayBoolean from "@base/Components/DisplayBoolean.vue";
import useFormHelper from "@tool/Helpers/useFormHelper";
import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import {BvModalEvent} from "bootstrap-vue/src/components/modal";
import moment from "moment";
import {validateState} from "@base/base.helpers";
import useAxios from "@tool/Helpers/useAxios";
import {useToast} from "vue-toastification/composition";
import {useInertia} from "@tool/Helpers/useInertia";
import {BuilderParameterInterface} from "@tool/Builder/Builder.types";
import useBuilderStore from "@tool/store/useBuilderStore";
import FormError from "@tool/Components/FormError.vue";
import SelectParametersGroup from "@tool/Components/SelectParametersGroup.vue";
import BlockMessage from "@base/Components/BlockMessage.vue";
import useRequiresUpgradeAlerts from "@tool/Helpers/useRequiresUpgradeAlerts";

export default defineComponent({
    components: {
        ProjectLayout,
        InertiaDatatable,
        ThemeBlock,
        DisplayBoolean,
        FormError,
        SelectParametersGroup,
        BlockMessage
    },
    props: {
        project: {
            type: Object as PropType<ProjectEntityInterface>,
            required: true
        },
        canManageTemplates: {
            type: Boolean,
            required: true
        },
        templates: {
            type: Object as PropType<ResourceCollectionInterface<TemplateEntityInterface>>,
            required: true
        },
        parameters: {
            type: Array as PropType<BuilderParameterInterface[]>,
            required: true
        }
    },
    setup(props) {

        //  Init parameters store
        const {
            projectParameters,
            formParameters,
            parametersObjectToArray,
            project,
            projectBuilderSettings,
            clearParameters,
        } = useBuilderStore();

        project.value = props.project;
        projectBuilderSettings.value = props.project.builder_settings;

        for (let i = 0; i < props.parameters.length; i++) {
            projectParameters.value[props.parameters[i].type].push(props.parameters[i]);
        }


        const name = 'UTM Templates';

        const table = useInertiaTable(props.templates, [
            {
                key: 'name',
                label: 'Name'
            },
            {
                key: 'parameters',
                label: 'Parameters'
            },
            {
                key: 'active',
                label: 'Suggest in the builder'
            },
            {
                key: 'actions',
                label: 'Actions',
                class: 'actions'
            },
        ]);

        const modalState = ref(false);

        const modalMode = ref<'create' | 'edit'>('create');

        const modalTitle = computed(() => {
            return (modalMode.value === 'create' ? 'Add new ' : 'Edit ') + ' UTM Template';
        });

        const modalOkTitle = computed(() => {
            return modalMode.value === 'create' ? 'Add' : 'Save';
        });

        const modalOkIsDisabled = computed(() => {
            return modalForm.value.processing || modalFormValidator.value.$invalid;
        });

        interface FormData {
            id: string;
            name: string;
            active: boolean;
            notes: string | null;
            created_by: string;
            created_on: string;
        }

        const modalForm = useFormHelper<FormData>({
            id: '',
            name: '',
            active: true,
            notes: '',
            created_by: '',
            created_on: '',
        });

        const modalFormValidator = useVuelidate({
            name: {
                required
            }
        }, modalForm.value.data);

        const toast = useToast();

        const modalHandleOk = async (event: BvModalEvent) => {
            event.preventDefault();

            if (modalMode.value === 'create') {

                const response = await modalForm.value.submit((data) => {
                    return useAxios().post<TemplateEntityInterface>(route('app.project.templates.store', {
                        tool_project_entity: props.project.slug,
                    }), {
                        name: data.name,
                        active: data.active,
                        notes: data.notes,
                        parameters: parametersObjectToArray(formParameters.value)
                    });
                });

                if (response.success) {
                    useInertia().get(route(route().current(), route().params), {}, {
                        preserveScroll: true,
                        onSuccess() {
                            toast.success('Template was successfully added to your project!');
                        }
                    });

                    return;
                }
            } else {
                const {success: successResponse} = await modalForm.value.submit((data) => {
                    return useAxios().put<TemplateEntityInterface>(route('app.project.templates.update', {
                        tool_project_entity: props.project.slug,
                        tool_template_entity: modalForm.value.data.id
                    }), {
                        name: data.name,
                        active: data.active,
                        notes: data.notes,
                        parameters: parametersObjectToArray(formParameters.value)
                    });
                });

                if (successResponse) {
                    useInertia().get(route(route().current(), route().params), {}, {
                        // Because it changes order anyways
                        preserveScroll: false,
                        onSuccess() {
                            toast.success('Template was successfully updated!');
                        }
                    });
                }

            }

        }

        const modalOpen = (template: WithProps<TemplateEntityInterface, 'user'> | null) => {
            if (!props.canManageTemplates) {
                return useRequiresUpgradeAlerts().templatesManagement(props.project.team_id);
            }

            modalMode.value = template ? 'edit' : 'create';

            clearParameters();

            if (template) {

                for (let i = 0; i < template.parameters.length; i++) {
                    // @SOMEDAY: Get notes of params as well
                    formParameters.value[template.parameters[i].type] = {
                        ...template.parameters[i],
                        notes: null
                    };
                }

                modalForm.value.data.id = template.id;
                modalForm.value.data.name = template.name;
                modalForm.value.data.active = template.active;
                modalForm.value.data.notes = template.notes;
                modalForm.value.data.created_by = template.user.name_first;
                modalForm.value.data.created_on = moment(template.created_at).format('MMMM Do YYYY');
                modalFormValidator.value.$touch();
            } else {
                modalForm.value.reset();
            }

            modalState.value = true;
        }

        const modalOnClose = () => {
            modalFormValidator.value.$reset();
        }

        const buildSimilar = (template: TemplateEntityInterface) => {
            if (!props.canManageTemplates) {
                return useRequiresUpgradeAlerts().templatesManagement(props.project.team_id);
            }

            useInertia().get(route('app.project.builder.index', {
                tool_project_entity: props.project.slug,
                build_from_template: template.id
            }));
        }

        return {
            name,
            table,

            // Modal
            modalState,
            modalMode,
            modalTitle,
            modalOkTitle,
            modalOkIsDisabled,

            modalForm,
            modalFormValidator,

            modalHandleOk,
            modalOpen,
            modalOnClose,
            validateState,

            // Build similar
            buildSimilar,
        }
    }
})
